import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Key as KeyIcon
} from '@mui/icons-material';
import { PaymentGateway, PaymentSettings } from '../../../../types/storeSettings';

interface PaymentGatewaysSectionProps {
  formData: PaymentSettings;
  setFormData: React.Dispatch<React.SetStateAction<PaymentSettings>>;
  editing: boolean;
}

const PaymentGatewaysSection: React.FC<PaymentGatewaysSectionProps> = ({
  formData,
  setFormData,
  editing
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [currentGateway, setCurrentGateway] = useState<PaymentGateway | null>(null);
  const [gatewayFormData, setGatewayFormData] = useState<PaymentGateway>({
    id: '',
    name: '',
    isActive: false,
    logo: ''
  });

  const handleToggleGateway = (gatewayId: string, checked: boolean) => {
    console.log(`Toggling gateway ${gatewayId} to ${checked}`);
    const updatedGateways = formData.paymentGateways.map(g =>
      g.id === gatewayId ? { ...g, isActive: checked } : g
    );
    setFormData(prev => ({
      ...prev,
      paymentGateways: updatedGateways
    }));
  };

  const handleToggleElectronicPayment = (checked: boolean) => {
    console.log(`Toggling enableElectronicPayment to ${checked}`);
    setFormData(prev => ({
      ...prev,
      enableElectronicPayment: checked
    }));
  };

  const handleOpenDialog = (gateway?: PaymentGateway) => {
    if (gateway) {
      setCurrentGateway(gateway);
      setGatewayFormData(gateway);
    } else {
      setCurrentGateway(null);
      setGatewayFormData({
        id: `gateway_${Date.now()}`,
        name: '',
        isActive: false,
        logo: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleOpenApiKeyDialog = (gateway: PaymentGateway) => {
    setCurrentGateway(gateway);
    setGatewayFormData(gateway);
    setApiKeyDialogOpen(true);
  };

  const handleCloseApiKeyDialog = () => {
    setApiKeyDialogOpen(false);
  };

  const handleGatewayFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGatewayFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveGateway = () => {
    if (currentGateway) {
      // تحديث بوابة موجودة
      const updatedGateways = formData.paymentGateways.map(g =>
        g.id === currentGateway.id ? gatewayFormData : g
      );
      setFormData(prev => ({
        ...prev,
        paymentGateways: updatedGateways
      }));
    } else {
      // إضافة بوابة جديدة
      setFormData(prev => ({
        ...prev,
        paymentGateways: [...prev.paymentGateways, gatewayFormData]
      }));
    }
    handleCloseDialog();
  };

  const handleSaveApiKeys = () => {
    const updatedGateways = formData.paymentGateways.map(g =>
      g.id === currentGateway?.id ? gatewayFormData : g
    );
    setFormData(prev => ({
      ...prev,
      paymentGateways: updatedGateways
    }));
    handleCloseApiKeyDialog();
  };

  const handleDeleteGateway = (gatewayId: string) => {
    const updatedGateways = formData.paymentGateways.filter(g => g.id !== gatewayId);
    setFormData(prev => ({
      ...prev,
      paymentGateways: updatedGateways
    }));
  };

  return (
    <Box sx={{ mt: 4, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
          بوابات الدفع الإلكتروني
        </Typography>
        {editing && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={() => handleOpenDialog()}
          >
            إضافة بوابة دفع
          </Button>
        )}
      </Box>
      
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enableElectronicPayment}
                onChange={(e) => handleToggleElectronicPayment(e.target.checked)}
                name="enableElectronicPayment"
                disabled={!editing}
                color="primary"
              />
            }
            label="تفعيل الدفع الإلكتروني"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            عند تفعيل الدفع الإلكتروني، يمكنك اختيار بوابات الدفع التي تريد استخدامها أدناه.
          </Typography>
        </Box>

        {formData.paymentGateways.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            لا توجد بوابات دفع. قم بإضافة بوابة دفع جديدة.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {formData.paymentGateways.map((gateway) => (
              <Grid item xs={12} sm={6} md={4} key={gateway.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        {gateway.name}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={gateway.isActive}
                            onChange={(e) => handleToggleGateway(gateway.id, e.target.checked)}
                            disabled={!editing || !formData.enableElectronicPayment}
                            size="small"
                          />
                        }
                        label="مفعل"
                      />
                    </Box>

                    {gateway.logo && (
                      <Box
                        component="img"
                        src={gateway.logo}
                        alt={gateway.name}
                        sx={{ 
                          height: 40, 
                          mb: 1, 
                          display: 'block',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          console.error(`Error loading image for ${gateway.name}:`, e);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}

                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {gateway.apiKey && (
                        <Chip
                          label="API Key مضبوط"
                          color="success"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      )}

                      {gateway.secretKey && (
                        <Chip
                          label="Secret Key مضبوط"
                          color="success"
                          size="small"
                        />
                      )}
                    </Box>
                  </CardContent>
                  
                  {editing && (
                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                      <Tooltip title="إعداد مفاتيح API">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenApiKeyDialog(gateway)}
                          color="primary"
                        >
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="تعديل">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDialog(gateway)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteGateway(gateway.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* مربع حوار إضافة/تعديل بوابة دفع */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentGateway ? 'تعديل بوابة دفع' : 'إضافة بوابة دفع جديدة'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="اسم بوابة الدفع"
            type="text"
            fullWidth
            variant="outlined"
            value={gatewayFormData.name}
            onChange={handleGatewayFormChange}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            name="logo"
            label="رابط شعار بوابة الدفع"
            type="text"
            fullWidth
            variant="outlined"
            value={gatewayFormData.logo || ''}
            onChange={handleGatewayFormChange}
            helperText="أدخل رابط URL لشعار بوابة الدفع"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSaveGateway} color="primary" variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* مربع حوار إعداد مفاتيح API */}
      <Dialog open={apiKeyDialogOpen} onClose={handleCloseApiKeyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          إعداد مفاتيح API لـ {currentGateway?.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="apiKey"
            label="API Key"
            type="text"
            fullWidth
            variant="outlined"
            value={gatewayFormData.apiKey || ''}
            onChange={handleGatewayFormChange}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            name="secretKey"
            label="Secret Key"
            type="text"
            fullWidth
            variant="outlined"
            value={gatewayFormData.secretKey || ''}
            onChange={handleGatewayFormChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseApiKeyDialog} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSaveApiKeys} color="primary" variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentGatewaysSection;
